/* eslint-disable @typescript-eslint/no-shadow */
import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import isNumber from 'lodash/isNumber';
import mapValues from 'lodash/mapValues';
import { ArrowForward } from '@yme/assets/icons';
import { Header } from '@yme/atoms/bottom-sheet';
import { BottomSheet } from '@yme/atoms/bottom-sheet/BottomSheet';
import { Button, CONTAINED_VARIANT, FADED_VARIANT, ICON_ONLY, NO_ICON, SIZE_44, } from '@yme/atoms/button';
import { Input } from '@yme/atoms/input';
import { Slider } from '@yme/atoms/slider';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { useFilterDropdownContext } from '@yme/molecules/filter-dropdown';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel2 } from '@yme/tokens/typography';
import { SeparatorStyled, WrapperStyled } from './styled';
const LOW_INPUT_NAME = 'RangePickerLowInput';
const HIGH_INPUT_NAME = 'RangePickerHighInput';
export const RangePicker = memo(({ min, max, low, high, valueSymbol, dataTestId, confirmLabel, isRightToLeft, onChange, onSubmit, }) => {
    const { isExpanded, titleLabel, setIsExpanded } = useFilterDropdownContext();
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const [values, setValues] = useState({
        low: min,
        high: max,
    });
    const [inputValues, setInputValues] = useState({
        low: String(min),
        high: String(max),
    });
    const setInitialState = useCallback(() => {
        setValues({
            low: min,
            high: max,
        });
        setInputValues({
            low: String(min),
            high: String(max),
        });
    }, [min, max]);
    // sync state with incoming values
    useEffect(() => {
        if (isNumber(low) && isNumber(high)) {
            setValues({
                low,
                high,
            });
            setInputValues({
                low: String(low),
                high: String(high),
            });
        }
        else {
            setInitialState();
        }
    }, [low, high, setInitialState]);
    useEffect(() => {
        if (!isExpanded && (low !== values.low || high !== values.high)) {
            setValues({
                low: low ?? min,
                high: high ?? max,
            });
            setInputValues({
                low: String(low ?? min),
                high: String(high ?? max),
            });
        }
    }, [isExpanded, high, low, max, min, values.high, values.low]);
    const handleOnSliderValueChanged = useCallback(({ low, high }) => {
        setValues({
            low,
            high,
        });
        setInputValues({
            low: String(low),
            high: String(high),
        });
        onChange?.({
            low,
            high,
        });
    }, [onChange]);
    const handleOnLowInputChanged = useCallback((value) => {
        setInputValues({
            low: value,
            high: inputValues.high,
        });
    }, [inputValues.high]);
    const handleOnHighInputChanged = useCallback((value) => {
        setInputValues({
            low: inputValues.low,
            high: value,
        });
    }, [inputValues.low]);
    const handleOnBlur = useCallback((event) => {
        const isLowInput = event?.target?.name === LOW_INPUT_NAME;
        if (Number(inputValues.high) < Number(inputValues.low) ||
            Number(inputValues.high) > max ||
            Number(inputValues.low) < min) {
            const nextValues = {
                low: isLowInput ? min : values.low,
                high: isLowInput ? values.high : max,
            };
            setValues(nextValues);
            setInputValues(mapValues(nextValues, String));
        }
        else {
            setValues({
                low: Number(inputValues.low),
                high: Number(inputValues.high),
            });
        }
    }, [inputValues.high, inputValues.low, max, min, values.high, values.low]);
    const handleOnSubmit = useCallback(() => {
        const { low, high } = values;
        onSubmit?.({
            low,
            high,
        });
    }, [onSubmit, values]);
    const handleMobileSubmit = useCallback(() => {
        handleOnSubmit();
        setIsExpanded(false);
    }, [handleOnSubmit, setIsExpanded]);
    const onDismiss = useCallback(() => {
        setIsExpanded(false);
    }, [setIsExpanded]);
    const inputRightIcon = useCallback(() => valueSymbol, [valueSymbol]);
    const Range = useMemo(() => (React.createElement("div", { "data-testid": dataTestId },
        React.createElement(WrapperStyled, null,
            React.createElement(Slider, { isRightToLeft: isRightToLeft, onChange: handleOnSliderValueChanged, min: min, max: max, low: values.low, high: values.high, dataTestId: `${dataTestId}_Slider` }),
            React.createElement(FlexWrapper, { mt: "26px", flexDirection: "row", alignItems: "center", justifyContent: "center" },
                React.createElement(Input, { isFullWidth: true, name: LOW_INPUT_NAME, placeholder: String(min), rightIcon: inputRightIcon, changeHandler: handleOnLowInputChanged, blurHandler: handleOnBlur, value: String(inputValues.low) }),
                React.createElement(SeparatorStyled, null),
                React.createElement(Input, { isFullWidth: true, name: HIGH_INPUT_NAME, placeholder: String(max), rightIcon: inputRightIcon, changeHandler: handleOnHighInputChanged, blurHandler: handleOnBlur, value: String(inputValues.high) }),
                isDesktop && (React.createElement(FlexWrapper, { ml: !isRightToLeft ? '12px' : '0', mr: isRightToLeft ? '12px' : '0' },
                    React.createElement(Button, { size: SIZE_44, buttonVariant: FADED_VARIANT, hasIconButton: ICON_ONLY, iconComponent: ArrowForward, onClick: handleOnSubmit, iconRotate: isRightToLeft ? 180 : 0 }))))))), [
        dataTestId,
        handleOnBlur,
        handleOnHighInputChanged,
        handleOnLowInputChanged,
        handleOnSliderValueChanged,
        handleOnSubmit,
        inputRightIcon,
        inputValues.high,
        inputValues.low,
        isDesktop,
        isRightToLeft,
        max,
        min,
        values.high,
        values.low,
    ]);
    if (!isDesktop) {
        return (React.createElement(BottomSheet, { open: isExpanded, onDismiss: onDismiss, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, titleLabel) }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: confirmLabel, onClick: handleMobileSubmit }) }, Range));
    }
    if (isDesktop && isExpanded) {
        return Range;
    }
    return null;
});
RangePicker.displayName = 'RangePicker';
